import { useEffect, useRef, useState } from 'react';
import { Application, Graphics, Text, Container } from 'pixi.js';

interface PieCategory {
  name: string;
  value: number;
  percentage: number;
}

interface PieFrame {
  timestamp: string;
  total: number;
  categories: PieCategory[];
}

interface AnimatedPieGraphProps {
  data: PieFrame[];
  backgroundColor: string;
  animationSpeed: number;
  shouldDraw: boolean;
  onRecordingComplete?: (blob: Blob) => void;
  labelFontSize: number;
  showLabels: boolean;
  colorPalette: string;
  captureVideo: boolean;
  showWatermark: boolean;
}

export function AnimatedPieGraph({
  data,
  backgroundColor,
  animationSpeed,
  shouldDraw,
  onRecordingComplete,
  labelFontSize,
  showLabels,
  colorPalette,
  captureVideo,
  showWatermark
}: AnimatedPieGraphProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application | null>(null);
  const animationRef = useRef<number | null>(null);
  const currentFrameIndexRef = useRef<number>(0);
  const isAnimatingRef = useRef<boolean>(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isAppReady, setIsAppReady] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);

  // Color palettes
  const colorPalettes = {
    default: [0xFF6B6B, 0x4ECDC4, 0x45B7D1, 0x96CEB4, 0xFFEAA7, 0xDDA0DD, 0x98D8C8, 0xF7DC6F],
    pastel: [0xFFB3BA, 0xBAFFC9, 0xBAE1FF, 0xFFFFBA, 0xFFB3F7, 0xB3FFE6, 0xF7B3FF, 0xB3F7FF],
    vivid: [0xFF0000, 0x00FF00, 0x0000FF, 0xFFFF00, 0xFF00FF, 0x00FFFF, 0xFF8000, 0x8000FF],
    monochrome: [0x333333, 0x666666, 0x999999, 0xCCCCCC, 0x444444, 0x777777, 0xAAAAAA, 0xDDDDDD]
  };

  const getColors = () => colorPalettes[colorPalette as keyof typeof colorPalettes] || colorPalettes.default;

  const startRecording = async () => {
    try {
      console.log('startRecording called, appRef.current:', appRef.current);
      
      if (!appRef.current || !appRef.current.canvas) {
        console.error('Cannot start recording: PixiJS Application canvas is undefined', appRef.current);
        return;
      }

      console.log('appRef.current.canvas exists:', appRef.current.canvas);
      console.log('appRef.current.canvas type:', typeof appRef.current.canvas);

      const mimeTypes = [
        'video/mp4;codecs=avc1.42E01E',
        'video/mp4;codecs=avc1.4D401E',
        'video/mp4;codecs=avc1.64001E',
        'video/mp4;codecs=h264',
        'video/mp4',
        'video/webm;codecs=h264',
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm'
      ];

      let selectedMimeType = null;
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }

      if (!selectedMimeType) {
        console.error('No supported video format found');
        return;
      }

      // Double-check before captureStream
      if (!appRef.current.canvas || typeof appRef.current.canvas.captureStream !== 'function') {
        console.error('captureStream not available on canvas:', appRef.current.canvas);
        return;
      }

      const stream = appRef.current.canvas.captureStream(30);
      const mediaRecorder = new MediaRecorder(stream, { mimeType: selectedMimeType });
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: selectedMimeType });
        recordedChunksRef.current = [];
        setIsRecording(false);
        if (onRecordingComplete) {
          onRecordingComplete(blob);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      console.log('Started recording pie chart animation');
    } catch (error) {
      console.error('Error in startRecording:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
  };

  const drawPieChart = (frame: PieFrame, progress: number = 1) => {
    try {
      if (!appRef.current || !appRef.current.stage || !appRef.current.view) {
        console.error('PixiJS app or stage or view is not initialized');
        return;
      }
      if (!frame || !Array.isArray(frame.categories)) {
        console.error('Frame or categories is not valid', frame);
        return;
      }

      const app = appRef.current;
      const stage = app.stage;
      const centerX = app.screen.width / 2;
      const centerY = app.screen.height / 2;
      const radius = Math.min(centerX, centerY) * 0.6;

      // Clear previous content
      stage.removeChildren();

      // Draw background rectangle
      const bg = new Graphics();
      let bgColor = 0xffffff;
      if (backgroundColor === 'black') bgColor = 0x000000;
      else if (backgroundColor === '#404040') bgColor = 0x404040;
      else if (backgroundColor === '#f0f0f0') bgColor = 0xf0f0f0;
      bg.beginFill(bgColor);
      bg.drawRect(0, 0, app.screen.width, app.screen.height);
      bg.endFill();
      stage.addChild(bg);

      if (frame.categories.length === 0) return;

      const colors = getColors();
      let currentAngle = -Math.PI / 2; // Start from top

      frame.categories.forEach((category, index) => {
        const sliceAngle = (category.percentage / 100) * 2 * Math.PI * progress;
        const color = colors[index % colors.length];

        // Draw pie slice
        const slice = new Graphics();
        slice.beginFill(color);
        slice.moveTo(centerX, centerY);
        slice.arc(centerX, centerY, radius, currentAngle, currentAngle + sliceAngle);
        slice.lineTo(centerX, centerY);
        slice.endFill();

        // Add border
        slice.setStrokeStyle({
          width: 2,
          color: 0x000000,
          alpha: 0.3
        });
        slice.moveTo(centerX, centerY);
        slice.arc(centerX, centerY, radius, currentAngle, currentAngle + sliceAngle);
        slice.lineTo(centerX, centerY);

        stage.addChild(slice);

        // Add label if enabled
        if (showLabels && progress > 0.5) {
          const labelAngle = currentAngle + sliceAngle / 2;
          const labelRadius = radius * 0.7;
          const labelX = centerX + Math.cos(labelAngle) * labelRadius;
          const labelY = centerY + Math.sin(labelAngle) * labelRadius;

          const label = new Text({
            text: `${category.name}\n${category.percentage.toFixed(1)}%`,
            style: {
              fontSize: labelFontSize,
              fill: 0x000000,
              align: 'center'
            }
          });
          label.anchor.set(0.5);
          label.x = labelX;
          label.y = labelY;
          stage.addChild(label);
        }

        currentAngle += sliceAngle;
      });

      // Add timestamp
      const timestampText = new Text({
        text: frame.timestamp,
        style: {
          fontSize: labelFontSize + 4,
          fill: backgroundColor === 'black' || backgroundColor === '#404040' ? 0xffffff : 0x000000,
          fontWeight: 'bold'
        }
      });
      timestampText.anchor.set(0.5);
      timestampText.x = centerX;
      timestampText.y = centerY + radius + 40;
      stage.addChild(timestampText);

      // Add total value
      const totalText = new Text({
        text: `Total: ${frame.total.toLocaleString()}`,
        style: {
          fontSize: labelFontSize,
          fill: backgroundColor === 'black' || backgroundColor === '#404040' ? 0xffffff : 0x000000
        }
      });
      totalText.anchor.set(0.5);
      totalText.x = centerX;
      totalText.y = centerY + radius + 60;
      stage.addChild(totalText);

      // Add watermark if needed
      if (showWatermark) {
        const watermark = new Text({
          text: 'graffy.ca',
          style: {
            fontSize: 16,
            fill: backgroundColor === 'black' || backgroundColor === '#404040' ? 0xffffff : 0x000000
          }
        });
        watermark.anchor.set(1);
        watermark.x = app.screen.width - 10;
        watermark.y = app.screen.height - 10;
        watermark.alpha = 0.7;
        stage.addChild(watermark);
      }
    } catch (err) {
      console.error('Error in drawPieChart:', err);
    }
  };

  const animate = () => {
    try {
      if (!shouldDraw || data.length === 0 || !isAppReady) {
        setIsAnimating(false);
        isAnimatingRef.current = false;
        return;
      }

      if (currentFrameIndexRef.current >= data.length) {
        // Animation complete
        setIsAnimating(false);
        isAnimatingRef.current = false;
        if (isRecording) {
          stopRecording();
        }
        return;
      }

      const frame = data[currentFrameIndexRef.current];
      drawPieChart(frame, 1);

      // Move to next frame after a delay
      setTimeout(() => {
        currentFrameIndexRef.current++;
        // Continue animation with next frame
        if (shouldDraw && isAnimatingRef.current) {
          animate();
        }
      }, 1000 / animationSpeed);

    } catch (err) {
      console.error('Error in animate:', err);
      setIsAnimating(false);
      isAnimatingRef.current = false;
    }
  };

  useEffect(() => {
    if (!containerRef.current) return;

    try {
      console.log('Creating PixiJS Application...');
      const app = new Application();
      
      // Get container dimensions
      const containerWidth = containerRef.current.clientWidth || 800;
      const containerHeight = containerRef.current.clientHeight || 600;
      
      console.log('Container dimensions for canvas:', containerWidth, 'x', containerHeight);
      
      // Initialize the application with the new method (matching AnimatedGraph.tsx)
      app.init({
        width: containerWidth,
        height: containerHeight,
        backgroundColor: 0xffffff,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
      }).then(() => {
        if (containerRef.current && app.canvas) {
          app.canvas.style.width = '100%';
          app.canvas.style.height = '100%';
          app.canvas.style.display = 'block';
          app.canvas.style.position = 'absolute';
          app.canvas.style.top = '0';
          app.canvas.style.left = '0';
          containerRef.current.appendChild(app.canvas);
          appRef.current = app;
          setIsAppReady(true);
          console.log('PixiJS Application initialized successfully');
        } else {
          console.error('No canvas found after initialization');
          setIsAppReady(false);
        }
      }).catch((error) => {
        console.error('Error initializing PixiJS Application:', error);
        setIsAppReady(false);
      });
    } catch (error) {
      console.error('Error creating PixiJS Application:', error);
      setIsAppReady(false);
    }

    return () => {
      if (appRef.current) {
        appRef.current.destroy(true);
        appRef.current = null;
      }
      setIsAppReady(false);
    };
  }, []);

  useEffect(() => {
    if (shouldDraw && data.length > 0 && !isAnimatingRef.current && isAppReady) {
      currentFrameIndexRef.current = 0;
      setIsAnimating(true);
      isAnimatingRef.current = true;
      
      if (captureVideo) {
        startRecording();
      }
      
      animate();
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [shouldDraw, data.length, isAppReady]); // Removed captureVideo and isAnimatingRef from dependencies

  useEffect(() => {
    if (!shouldDraw) {
      setIsAnimating(false);
      isAnimatingRef.current = false;
      currentFrameIndexRef.current = 0;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
  }, [shouldDraw]);

  return (
    <div 
      ref={containerRef} 
      style={{ 
        width: '100%', 
        height: '600px',
        position: 'relative',
        overflow: 'hidden',
        margin: '0',
        padding: '0'
      }}
    />
  );
} 